import { combineEpics, ActionsObservable, StateObservable, ofType } from 'redux-observable';
import { RootAction, RootState } from '@src/app/_redux';
import { ApiClient } from '@src/app/_global/utility/apiUtils';
import { ApiConfig } from '@src/app/_global/domain';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { concat } from 'rxjs/observable/concat';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { actionCreators, ORDERFORECAST_START, PROMOCODEINQ_START, RENEWALS_START } from '@src/app/accounts/renewals/actions';
import { actionCreators as weakActionCreators } from '@src/app/login/weak/actions';
import { actionCreators as gaActionCreators } from '@src/app/analytics/googleAnalytics/actions';
import { getLoggedInCustomer } from '@src/app/accounts/selectors';
import { OrderForecastPostBody, OrderItem, RenewalPostBody } from '@src/_api/payload/renewals';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { PromotionCodeInqBody } from '@src/_api/payload/promotions';

const RENEWALS_PATH = '/renewal';
const PROMOCODEINQ_PATH = '/promocodeinquiry';
const getAllSucceeded = (...responses) => {
  const successResponses = responses.filter(item => 
    item.success === true);
  return successResponses;
};
const getAllPromoCodeInqSucceeded = (...responses) => {
  const successResponses = responses.filter(item => item.success);
  return successResponses.map(resp => {
    return resp.promotionCode;
  });
};
const getAllOrderForecastSucceeded = (...responses) => {
  const successResponses = responses.filter(item => item.success);
  return successResponses.map(resp => {
    return resp;
  });
};
export const startRenewal =  (
  action$: ActionsObservable<RootAction>,
  state$: StateObservable<RootState>,
) =>
    action$.pipe(
      ofType(RENEWALS_START),
      mergeMap(({ payload }) => {
      const client = new ApiClient();
      const clientConfig = state$.value.clientConfig.clientConfig;
      const loading = of(actionCreators.renewalsLoading(true));

      const promises = payload.renewalPayloads.map(renewal => {
        let putBody: RenewalPostBody = renewal;

        const apiConfig: ApiConfig = {
          credentialsObject: state$.value.auth.omsAuth.credentials,
          providerType: clientConfig.providerType,
          url: clientConfig.url,
          path: RENEWALS_PATH,
          region: clientConfig.Region,
          requestmethod: 'POST',
          requestbody: putBody,
          parms: {},
          additionalParms: {
            headers: {
              'x-session-id': clientConfig.uuid
            },
          },
        };
        return fromPromise(client.callApiGateway(apiConfig));
      });
      let loggedIncustomer = getLoggedInCustomer(state$.value);
      const refetchCustomerPayload = {
        accountNumber: loggedIncustomer.accountNumber,
        postalCode: loggedIncustomer.postalCode,
      };
      let reloadCust = of(weakActionCreators.refetchCustomer(refetchCustomerPayload));
      const source$ = forkJoin(promises, getAllSucceeded).pipe(
        map(responses => {
          let payloadCount = payload.renewalPayloads.length;
          let gaCategory = payload.renewalPayloads[0].googleAnalyticsCategory;
          if (payloadCount === responses.length) {
            responses.forEach(renewalResponse => {
              let matchingRenewalPayload = payload.renewalPayloads.find((renewalPayload) => 
                renewalPayload.orderItems[0].choiceCode === renewalResponse.order.orderItems[0].choiceCode &&
                renewalPayload.orderItems[0].itemNumber === renewalResponse.order.orderItems[0].itemNumber &&
                renewalPayload.orderItems[0].accountNumber === renewalResponse.order.orderItems[0].recipient.accountNumber);
              gaActionCreators.createGAEvent({
                category: gaCategory,
                action: 'Successful',
                value:  renewalResponse.order.payment.amountPaid * 100,  // value can only be an integer
                label: matchingRenewalPayload.googleAnalyticsLabel
              });
            });
            return actionCreators.renewalsSuccess({payload, responses, success: true});
          } else {
            responses.forEach(renewalResponse => {
              let matchingRenewalPayload = payload.renewalPayloads.find((renewalPayload) => 
                renewalPayload.orderItems[0].choiceCode === renewalResponse.order.orderItems[0].choiceCode &&
                renewalPayload.orderItems[0].itemNumber === renewalResponse.order.orderItems[0].itemNumber &&
                renewalPayload.orderItems[0].accountNumber === renewalResponse.order.orderItems[0].recipient.accountNumber);
              gaActionCreators.createGAEvent({
                category: gaCategory,
                action: 'Unsuccessful',
                value:  renewalResponse.order.payment.amountPaid * 100,
                label: matchingRenewalPayload.googleAnalyticsLabel
              });
            });
            return actionCreators.renewalsError({
              success: false,
              message: 'Error on Renewal',
            });
          }
        }),
        catchError(err => {
          gaActionCreators.createGAEvent({
            category:  payload.renewalPayloads[0].googleAnalyticsCategory,
            action: 'Unknown Error',
            label: payload.renewalPayloads[0].googleAnalyticsLabel
          });
          return of(
            actionCreators.renewalsError({ success: false, message: err }),
          );
        }),
      );
      return concat(loading, source$, reloadCust);
    }),
  );
  export const promotionCodeInquiry = (
    action$: ActionsObservable<RootAction>,
    state$: StateObservable<RootState>,
  ) =>
    action$.pipe(
      ofType(PROMOCODEINQ_START),
      mergeMap(({ payload }) => {
        const clientConfig = state$.value.clientConfig.clientConfig;
        const loading = of(actionCreators.promoCodeInqLoading(true));
        const client = new ApiClient();
  
        const promises = payload.promoCodeInqOrders.map((item: any) => { 
          let postBody: PromotionCodeInqBody = {
            prodIdAlias: payload.customer.prodIdAlias,
            promotionCode: item.promotionKey,
          };
          const apiConfig: ApiConfig = {
            credentialsObject: state$.value.auth.omsAuth.credentials,
            providerType: clientConfig.providerType,
            url: clientConfig.url,
            path: PROMOCODEINQ_PATH,
            region: clientConfig.Region,
            requestmethod: 'POST',
            requestbody: postBody,
            parms: {},
            additionalParms: {
              headers: {
                'x-session-id': clientConfig.uuid
              },
            },
          };
          return fromPromise(client.callApiGateway(apiConfig));
        });
  
        const source$ = forkJoin(promises, getAllPromoCodeInqSucceeded).pipe(
          map(allSucceeded => {
            if (allSucceeded) {
              return actionCreators.promoCodeInqSuccess({promotionCodes: allSucceeded});
            } else {
              return actionCreators.promoCodeInqError({
                success: false,
                message: 'Error on Promotion Code Inq',
              });
            }
          }),
          catchError(err => {
            gaActionCreators.createGAEvent({
              category: payload.promoCodeInqOrders[0].googleAnalyticsCategory,
              action: 'Unknown Error',
              label: payload.promoCodeInqOrders[0].prodIdAlias
            });
            return of(
              actionCreators.promoCodeInqError({ success: false, message: err }),
            );
          }),
        );
        return concat(loading, source$);
      }),
    );

export const startOrderForecast = (
  action$: ActionsObservable<RootAction>,
  state$: StateObservable<RootState>,
) =>
  action$.pipe(
    ofType(ORDERFORECAST_START),
    mergeMap(({ payload }) => {
      const clientConfig = state$.value.clientConfig.clientConfig;
      const loading = of(actionCreators.orderForecastLoading(true));
      const client = new ApiClient();

      const promises = payload.renewalOrders.map((renewalOrder: any) => {
        const promotionCode = payload.promoCodeInqResult?.promotionCodes.find(
          (promoCode: any) =>
            renewalOrder.promotionKey === promoCode.promotionCode
        );

        const recipAccount = payload.associatedGifts.associatedGiftAccounts[renewalOrder.shipToAccountNumber];

        let allOrderItems: any[] = [];
        if (promotionCode) {
          promotionCode.products.forEach((product: any) => {
            product.choices?.forEach((choice: any) => {
              allOrderItems.push({
                itemNumber: choice.itemNumber,
                quantity: '1',
                choiceCode: choice.choiceCode,
                accountNumber: recipAccount ? recipAccount.accountNumber : payload.customer.accountNumber,
                addressId: recipAccount ? recipAccount.address.addressId : payload.customer.address.addressId,
                currencyCode: choice.currencyCode,
              });
            });
          });
        }

        // Filter by currency
        const subscriberCurrency = renewalOrder.currencyCode;
        allOrderItems = allOrderItems.filter(
          (item) => item.currencyCode === subscriberCurrency
        );

        // Skip if no items
        if (allOrderItems.length === 0) {
          return of(undefined);
        }

        // Group by item number
        const grouped: { [key: string]: any[] } = {};
        for (const item of allOrderItems) {
          if (!grouped[item.itemNumber + item.choiceCode]) {
            grouped[item.itemNumber + item.choiceCode] = [];
          }
          grouped[item.itemNumber + item.choiceCode].push(item);
        }

        // Create calls per group
        const callsPerGroup = Object.entries(grouped).map(([_, items]) => {
          const postBody: OrderForecastPostBody = {
            prodIdAlias: payload.customer.prodIdAlias,
            promotionCode: renewalOrder.promotionKey,
            accountNumber: payload.customer.accountNumber,
            addressId: payload.customer.address.addressId,
            orderItems: items as OrderItem[],
            systemCode: 'CORE',
            coreTransactionType: 'orderForecast',
            isRenewalOrderForcast: true,
          };

          const apiConfig: ApiConfig = {
            credentialsObject: state$.value.auth.omsAuth.credentials,
            providerType: clientConfig.providerType,
            url: clientConfig.url,
            path: RENEWALS_PATH,
            region: clientConfig.Region,
            requestmethod: 'POST',
            requestbody: postBody,
            parms: {},
            additionalParms: {
              headers: {
                'x-session-id': clientConfig.uuid,
              },
            },
          };

          return fromPromise(client.callApiGateway(apiConfig));
        });

        // Return all grouped calls
        return forkJoin(callsPerGroup).pipe(
          map((responses) => responses)
        );
      });

      return concat(
        loading,
        forkJoin(promises).pipe(
          map((responses) => {
            const flatResponses = responses.reduce<any[]>((acc: any[], val: any) => acc.concat(val), []);
            const successResponses = getAllOrderForecastSucceeded(
              ...flatResponses
            );
            return actionCreators.orderForecastSuccess({
              orderForecast: successResponses,
            });
          }),
          catchError((err) => {
            gaActionCreators.createGAEvent({
              category: 'Order Forecast',
              action: 'Unknown Error',
              label: payload.renewalOrders[0]?.prodIdAlias || payload.customer.prodIdAlias,
            });
            return of(
              actionCreators.orderForecaseError({
                success: false,
                message: err,
              })
            );
          })
        )
      );
    })
  );
  
export const epics = combineEpics(startRenewal, promotionCodeInquiry, startOrderForecast);
